import { PlanningActionMetadataDtoModelType, PlanningActionsStatus } from '../../../models';
import * as ScheduleNotifications from '../../../../../notifications/scheduling/schedule';
import getAppEnv from '../../../utils/getAppEnv';
import getAppRoot from '../../../utils/getAppRoot';
import { TopMenuView } from '../../../../../components/scheduling/ViewMenu';

type AppStore = ReturnType<typeof getAppRoot>;
type Model = AppStore['model'];
type UI = AppStore['ui'];
type AppEnv = ReturnType<typeof getAppEnv>;

const handleDifferentDateOptimization = async (model: Model, ui: UI, projectId: string) => {
  // Clear unscheduled visits for different date optimization and fetch fresh data for current selected date
  model.clearUnscheduledVisits();
  ui.setIsOrdersLoadingOnInit(true);
  await model.fetchUnscheduledVisitsLatLngs(projectId);
  ui.setIsOrdersLoadingOnInit(false);
};

const handleSameDateOptimization = async (model: Model, ui: UI, projectId: string) => {
  // Fetch routes and extra data for the currently selected date
  await model.fetchProjectRoutes(projectId, ui.selectedDate);
  model.fetchProjectRoutesExtraData(projectId, ui.selectedDate);
};

// OptimizeRoutes / DrawRoute don't refetch the day's routes on finish: the per-route subscription
// already carries their solution, and the view stays where it is.
const skipsRouteRefresh = (planningAction: PlanningActionMetadataDtoModelType) =>
  planningAction.actionName === 'OptimizeRoutes' || planningAction.actionName === 'DrawRoute';

const handlePostOptimizationDataRefresh = async (
  model: Model,
  ui: UI,
  planningAction: PlanningActionMetadataDtoModelType,
  selectedDate: string | undefined,
  projectId: string,
) => {
  if (skipsRouteRefresh(planningAction)) return;

  ui.setCurrentView(TopMenuView.SCHEDULED);

  if (selectedDate !== planningAction.startDate) {
    await handleDifferentDateOptimization(model, ui, projectId);
  } else {
    await handleSameDateOptimization(model, ui, projectId);
  }
};

const handleFinishedStatus = async (
  appStore: AppStore,
  appEnv: AppEnv,
  fullPlanningAction: PlanningActionMetadataDtoModelType,
  selectedDate: string | undefined,
  projectId: string,
) => {
  const { model, ui } = appStore;

  ui.setIsOptimizing(false);
  ui.setSchedulingError(null);

  await handlePostOptimizationDataRefresh(model, ui, fullPlanningAction, selectedDate, projectId);

  // A finished optimisation is a settle point for the departure-time order. The kinds that refetch
  // the day's routes above settle inside fetchProjectRoutes; the two that skip it settle here.
  if (skipsRouteRefresh(fullPlanningAction) && selectedDate === fullPlanningAction.startDate) {
    appStore.timeline.settleRouteOrder();
  }

  appEnv.notification(ScheduleNotifications.scheduleSuccessNotification(
    fullPlanningAction.actionUuid,
    fullPlanningAction.output,
  ));

  if (selectedDate === fullPlanningAction.startDate) {
    appStore.planningActions.setMapBoundsBasedOnAction(fullPlanningAction);
  }
};

export default (fullPlanningAction: PlanningActionMetadataDtoModelType) => async () => {
  const { store } = fullPlanningAction;
  const appStore = getAppRoot(store);
  const appEnv = getAppEnv(store);
  const { ui } = appStore;
  const maybeProjectId = ui.selectedWorkspace?.id;
  const { selectedDate } = ui;

  if (!maybeProjectId || maybeProjectId !== String(fullPlanningAction.projectId)) {
    return;
  }

  // Type narrowing: projectId is guaranteed to be defined after the guard above
  const projectId: string = maybeProjectId;

  switch (fullPlanningAction.status) {
    case PlanningActionsStatus.NOT_STARTED:
    case PlanningActionsStatus.IN_PROGRESS: {
      ui.setIsOptimizing(true);
      ui.setSchedulingError(null);
      return;
    }
    case PlanningActionsStatus.FAILED:
    case PlanningActionsStatus.FINISHED_WITH_FAILED: {
      ui.setIsOptimizing(false);
      // Persist the reason so the Unscheduled panel can show it after the toast is gone.
      ui.setSchedulingError(ScheduleNotifications.buildOutputError(fullPlanningAction.output));
      appEnv.notification(ScheduleNotifications.scheduleFailureNotification(
        projectId,
        fullPlanningAction.output,
      ));
      break;
    }
    case PlanningActionsStatus.FINISHED: {
      await handleFinishedStatus(appStore, appEnv, fullPlanningAction, selectedDate, projectId);
      break;
    }
    default:
  }

  // Always query for lastUndoable and nextRedoable actions
  appStore.planningActions.getUndoableRedoableActions(true);
};
